import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { TableauBord } from '../../types/ndf';
import { BoutonPrincipal, Champ, dateFr, Etiquette, Saisie, Selection } from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { Bloc, Cumuls, euros, Indicateur } from '../../components/ndf/ui/Graphiques';
import { STATUTS_NOTE } from './NdfMesNotesPage';

/**
 * Tableau de bord des notes de frais (specs G1 à G4).
 *
 * Trois vues, un seul écran : collaborateur, manager, RH. Les blocs changent,
 * les FILTRES sont communs et s'appliquent à tout — sans quoi deux blocs
 * afficheraient deux périmètres et leurs totaux ne se recouperaient pas.
 *
 * Les cumuls sont CLIQUABLES : choisir une ligne filtre l'écran entier. C'est
 * ce qui distingue un tableau de bord qu'on interroge d'un écran qu'on subit.
 */
export function NdfTableauBordPage() {
  const navigate = useNavigate();
  const [vue, setVue] = useState<'collaborateur' | 'manager' | 'rh'>('collaborateur');
  const [filtres, setFiltres] = useState({
    du: '',
    au: '',
    salarie_id: '',
    categorie_id: '',
    client: '',
    facturable: '',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'tableau-bord', vue, filtres],
    queryFn: () => ndfApi.tableauBord({ vue, ...filtres }),
    retry: false,
  });

  const filtreActif = Object.values(filtres).some(Boolean);
  const effacer = () =>
    setFiltres({ du: '', au: '', salarie_id: '', categorie_id: '', client: '', facturable: '' });

  if (isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!data) return null;

  const estCollaborateur = data.vue === 'collaborateur';

  return (
    <div className="space-y-4 pb-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <h1 className="font-titre text-lg font-bold text-nuit">Tableau de bord</h1>

        {/* La bascule n'apparaît que si l'on a plusieurs vues : un onglet
            unique n'est pas un choix, c'est du bruit. */}
        {data.vues_disponibles.length > 1 && (
          <div className="flex gap-1 rounded-lg border border-nuit/15 p-1">
            {data.vues_disponibles.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVue(v as typeof vue)}
                className={`min-h-11 rounded-md px-3 text-sm ${
                  data.vue === v ? 'bg-nuit font-semibold text-white' : 'text-nuit'
                }`}
              >
                {v === 'collaborateur' ? 'Mes frais' : v === 'manager' ? 'Mon équipe' : 'RH'}
              </button>
            ))}
          </div>
        )}
      </header>

      {estCollaborateur && (
        <div className="grid gap-2 sm:grid-cols-2">
          <BoutonPrincipal type="button" onClick={() => navigate('/notes-de-frais/depense/nouvelle')}>
            Nouvelle dépense
          </BoutonPrincipal>
          <BoutonPrincipal type="button" onClick={() => navigate('/notes-de-frais/mes-notes')}>
            Nouvelle note de frais
          </BoutonPrincipal>
        </div>
      )}

      {/* ─── Filtres GLOBAUX : ils s'appliquent à tous les blocs ─────────── */}
      <section className="grid gap-3 rounded-lg border border-nuit/10 bg-white p-4 sm:grid-cols-3 lg:grid-cols-6">
        <Champ label="Du">
          <Saisie type="date" value={filtres.du} onChange={(e) => setFiltres({ ...filtres, du: e.target.value })} />
        </Champ>
        <Champ label="Au">
          <Saisie type="date" value={filtres.au} onChange={(e) => setFiltres({ ...filtres, au: e.target.value })} />
        </Champ>
        {!estCollaborateur && (
          <Champ label="Salarié">
            <Selection value={filtres.salarie_id} onChange={(e) => setFiltres({ ...filtres, salarie_id: e.target.value })}>
              <option value="">Tous</option>
              {data.options.salaries.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom}
                </option>
              ))}
            </Selection>
          </Champ>
        )}
        <Champ label="Catégorie">
          <Selection value={filtres.categorie_id} onChange={(e) => setFiltres({ ...filtres, categorie_id: e.target.value })}>
            <option value="">Toutes</option>
            {data.options.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.libelle}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Client">
          <Selection value={filtres.client} onChange={(e) => setFiltres({ ...filtres, client: e.target.value })}>
            <option value="">Tous</option>
            {data.options.clients.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Refacturable">
          <Selection value={filtres.facturable} onChange={(e) => setFiltres({ ...filtres, facturable: e.target.value })}>
            <option value="">Peu importe</option>
            <option value="oui">Oui</option>
            <option value="non">Non</option>
          </Selection>
        </Champ>
        {filtreActif && (
          <div className="sm:col-span-3 lg:col-span-6">
            <button type="button" onClick={effacer} className="min-h-11 text-sm text-nuit underline">
              Effacer tous les filtres
            </button>
          </div>
        )}
      </section>

      {/* ─── Indicateurs ────────────────────────────────────────────────── */}
      <div className={`grid gap-3 ${data.indicateurs.length > 3 ? 'lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
        {data.indicateurs.map((i) => (
          <Indicateur
            key={i.cle}
            libelle={i.libelle}
            valeur={i.unite === 'euros' ? euros(i.valeur) : String(i.valeur)}
            aide={i.aide}
            teinte={i.teinte as never}
          />
        ))}
      </div>

      {/* ─── Ce qui attend une action, avant les chiffres ────────────────── */}
      {!estCollaborateur && (
        <Bloc titre="Notes de frais à approuver">
          {data.notes.filter((n) => n.statut.startsWith('attente')).length === 0 ? (
            <p className="text-sm text-nuit/70">Rien n’attend de décision.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.notes
                .filter((n) => n.statut.startsWith('attente'))
                .sort((a, b) => (b.jours_attente ?? 0) - (a.jours_attente ?? 0))
                .map((n) => (
                  <li key={n.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-nuit/5 pb-1 last:border-0">
                    <Link to={`/notes-de-frais/note/${n.id}`} className="text-nuit underline">
                      {n.numero} : {n.salarie}
                    </Link>
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-nuit">{euros(n.total_a_rembourser)}</span>
                      <Etiquette ton={(n.jours_attente ?? 0) >= 5 ? 'alerte' : 'neutre'}>
                        {n.jours_attente ?? 0} j
                      </Etiquette>
                      <Etiquette ton="actif">
                        {n.statut === 'attente_manager' ? 'Manager' : 'RH'}
                      </Etiquette>
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </Bloc>
      )}

      {estCollaborateur && data.depenses_non_associees.length > 0 && (
        <Bloc
          titre={`Dépenses non associées à une note (${data.depenses_non_associees.length})`}
          action={
            <Link to="/notes-de-frais/mes-depenses" className="text-sm text-nuit underline">
              Les ranger
            </Link>
          }
        >
          <ul className="space-y-1 text-sm">
            {data.depenses_non_associees.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-nuit/5 pb-1 last:border-0">
                <Link to={`/notes-de-frais/depense/${d.id}`} className="min-w-0 truncate text-nuit underline">
                  {d.categorie} : {d.description || 'sans description'}
                </Link>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-nuit/60">{dateFr(d.date_depense)}</span>
                  <span className="font-medium text-nuit">{euros(d.montant_ttc)}</span>
                  {d.facturable && <Etiquette ton="actif">{d.client_nom ?? 'client à préciser'}</Etiquette>}
                </span>
              </li>
            ))}
          </ul>
        </Bloc>
      )}

      {/* ─── Kanban : de vraies colonnes, qui défilent ──────────────────── */}
      <Bloc titre="Notes de frais par statut">
        {/* Le défilement HORIZONTAL est porté par ce conteneur, et lui seul :
            les sept colonnes ne tiennent sur aucun écran, et les empiler en
            grille ferait perdre la lecture par colonne : qui est tout l'intérêt
            d'un kanban. */}
        <div className="-mx-4 overflow-x-auto px-4 pb-2">
          <div className="flex min-w-max gap-3">
            {data.kanban.map((colonne) => {
              const meta = STATUTS_NOTE[colonne.statut];
              const total = colonne.notes.reduce((s2, n) => s2 + n.total_a_rembourser, 0);
              return (
                <div
                  key={colonne.statut}
                  className="flex w-64 shrink-0 flex-col rounded-md bg-nuit/[0.03] p-2"
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <Etiquette ton={meta?.ton ?? 'neutre'}>
                      {meta?.libelle ?? colonne.statut}
                    </Etiquette>
                    <span className="text-sm font-medium text-nuit/60">{colonne.notes.length}</span>
                  </div>

                  {/* Le total de la colonne : dans un kanban, c'est ce qu'on
                      cherche après le nombre : « combien attend là ? ». */}
                  {colonne.notes.length > 0 && (
                    <p className="mb-2 text-xs text-nuit/60">{euros(total)} à rembourser</p>
                  )}

                  <ul className="flex flex-1 flex-col gap-2 overflow-y-auto" style={{ maxHeight: '28rem' }}>
                    {colonne.notes.length === 0 && (
                      <li className="py-4 text-center text-xs text-nuit/40">Aucune note</li>
                    )}
                    {colonne.notes.map((n) => (
                      <li key={n.id}>
                        <Link
                          to={`/notes-de-frais/note/${n.id}`}
                          className="block rounded-md border border-nuit/10 bg-white p-2 text-xs active:translate-y-px"
                        >
                          <span className="block truncate font-titre font-bold text-nuit">
                            {n.titre || n.numero}
                          </span>
                          <span className="block text-nuit/60">
                            {n.titre ? `${n.numero} · ` : ''}
                            {n.nombre_depenses} dépense{n.nombre_depenses > 1 ? 's' : ''}
                          </span>
                          {!estCollaborateur && (
                            <span className="block truncate text-nuit/60">{n.salarie}</span>
                          )}
                          <span className="mt-1 block font-medium text-nuit">
                            {euros(n.total_a_rembourser)}
                          </span>
                          {n.date_remboursement && (
                            <span className="block text-nuit/50">
                              remboursée le {dateFr(n.date_remboursement)}
                            </span>
                          )}
                          {n.jours_attente !== null && n.statut.startsWith('attente') && (
                            <span
                              className={`block ${n.jours_attente >= 5 ? 'text-corail' : 'text-nuit/50'}`}
                            >
                              en attente depuis {n.jours_attente} jour
                              {n.jours_attente > 1 ? 's' : ''}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </Bloc>

      {/* ─── Cumuls : les mêmes chiffres, sans habillage ─────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Bloc titre="Par catégorie">
          <Cumuls
            lignes={data.graphiques.par_categorie}
            selection={data.options.categories.find((c) => c.id === filtres.categorie_id)?.libelle ?? null}
            onChoisir={(l) => {
              const cat = data.options.categories.find((c) => c.libelle === l);
              setFiltres({ ...filtres, categorie_id: cat && cat.id !== filtres.categorie_id ? cat.id : '' });
            }}
          />
        </Bloc>

        <Bloc titre="Par mois">
          <Cumuls
            lignes={[...data.graphiques.par_mois].sort((a, b) => a.libelle.localeCompare(b.libelle))}
          />
        </Bloc>

        {!estCollaborateur && (
          <Bloc titre="Cumul par salarié">
            <Cumuls
              lignes={data.graphiques.par_salarie}
              selection={data.options.salaries.find((s2) => s2.id === filtres.salarie_id)?.nom ?? null}
              onChoisir={(l) => {
                const s2 = data.options.salaries.find((x) => x.nom === l);
                setFiltres({ ...filtres, salarie_id: s2 && s2.id !== filtres.salarie_id ? s2.id : '' });
              }}
            />
          </Bloc>
        )}

        <Bloc titre="Refacturable ou non">
          <Cumuls
            lignes={data.graphiques.refacturable}
            selection={
              filtres.facturable === 'oui'
                ? 'Refacturable'
                : filtres.facturable === 'non'
                  ? 'Non refacturable'
                  : null
            }
            onChoisir={(l) => {
              const cible = l === 'Refacturable' ? 'oui' : 'non';
              setFiltres({ ...filtres, facturable: filtres.facturable === cible ? '' : cible });
            }}
          />
        </Bloc>

        {data.graphiques.par_client.length > 0 && (
          <Bloc titre="Refacturable par client">
            <Cumuls
              lignes={data.graphiques.par_client}
              selection={filtres.client || null}
              onChoisir={(l) => setFiltres({ ...filtres, client: l === filtres.client ? '' : l })}
            />
          </Bloc>
        )}

        {data.graphiques.abonnements.length > 0 && (
          <Bloc titre="Abonnements de transport">
            <Cumuls lignes={data.graphiques.abonnements} />
          </Bloc>
        )}

        {data.cumuls_ik.length > 0 && (
          <Bloc titre={`Kilomètres déclarés (seuil ${data.seuil_ik} km)`}>
            <Cumuls
              lignes={data.cumuls_ik.map((c) => ({ libelle: c.salarie, montant: c.km }))}
              format={(v) => `${v} km`}
            />
            {data.cumuls_ik.some((c) => c.alerte) && (
              <p className="mt-2 text-sm text-jaune">
                Au-delà de {data.seuil_ik} km, un justificatif d’usage professionnel du véhicule peut
                être demandé.
              </p>
            )}
          </Bloc>
        )}
      </div>
    </div>
  );
}
